import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { waitForElement } from "../../utils/GlobalFunctions.js";
import { STORE_REPEATING_NAME } from "../../utils/Constants.js";

const prevPXToVW = (prevPX) => {
	if (!prevPX) return 0;
	return (parseFloat(prevPX) / window.innerWidth) * 100 || 0;
};

// This function creates a new repeating-name animation from the current position in the previous animation
// (called on page load for continuous looping)
const createKeyframeAnimation = (repeatingNameDiv, prevProgress) => {
	const prevVM = prevPXToVW(prevProgress);
	const animationName = `panAnimation_${Date.now()}`;
	const keyframes = `
	@keyframes ${animationName} {
		0% {
			background-position: ${prevVM%100}vw 0vh;
		}
		100% {
			background-position: ${prevVM%100 + 100}vw 0vh;
		}
	}`;

	// Create a <style> element for the animation
	const styleEl = document.createElement('style');
	document.head.appendChild(styleEl);

	// Get the sheet (safe, same-origin)
	const styleSheet = styleEl.sheet;
	styleSheet.insertRule(keyframes, styleSheet.cssRules.length);

	// Apply the animation to the element
	repeatingNameDiv.style.animation = `${animationName} 140s linear infinite`;
};

// This component must be a child of Router for the use functions to work
const RouteListener = () => {
	const location = useLocation();

	// Edit the canonical link for SPA pages to avoid duplicate content issues with search engines
	useEffect(() => {
		const pathname = location.pathname.replace(/\/+$/, "") || "/";
		const canonicalUrl = `https://www.hanoverholyname.org${pathname}`;
		const canonicalLink = document.querySelector('link[rel="canonical"]');

		if (canonicalLink) canonicalLink.href = canonicalUrl;

		const openGraphUrl = document.querySelector('meta[property="og:url"]');
		if (openGraphUrl) openGraphUrl.content = canonicalUrl;
	}, [location.pathname]);

	useEffect(() => {
		waitForElement('#repeating-name').then((repeatingNameDiv) => {
			// Restore repeating-name position when this component mounts
			const prevProgress = localStorage.getItem(STORE_REPEATING_NAME);
			createKeyframeAnimation(repeatingNameDiv, prevProgress);

			// Save repeating-name position before routing away
			const handleBeforeUnload = () => {
				const computedStyle = window.getComputedStyle(repeatingNameDiv);
				localStorage.setItem(STORE_REPEATING_NAME, computedStyle.backgroundPositionX);
				console.log("Saved background position:", computedStyle.backgroundPosition);
			};
			window.addEventListener("beforeunload", handleBeforeUnload);

			return () => {
				console.log("Cleaning up after route change...", repeatingNameDiv);
				window.removeEventListener("beforeunload", handleBeforeUnload);
			};
		});
	}, [location]);

	return null; // This component does not render anything
};

export default RouteListener;
